import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Routes,
  Route,
  Navigate,
  useNavigate,
  useLocation,
} from "react-router-dom";
import keycloak from "./auth/keycloak";
import { api } from "./api/client";
import { S } from "./styles";
import { hasRole, primaryRole } from "./auth/roleHelpers";
import { MeContext } from "./auth/MeContext";
import { ProtectedRoute } from "./auth/ProtectedRoute";
import { LoginPage } from "./auth/LoginPage";
import { HealthPanel } from "./features/status/HealthPanel";
import { PullTraxPanel } from "./features/rfqs/PullTraxPanel";
import { DemandEntry } from "./features/rfqs/DemandEntry";
import { RFQList } from "./features/rfqs/RFQList";
import { RequirementsPanel } from "./features/requirements/RequirementsPanel";
import { PartsPanel } from "./features/parts/PartsPanel";
import { QuoteComparisonRoute } from "./features/rfqs/QuoteComparisonRoute";
import { RecommendationsPanel } from "./features/recommendations/RecommendationsPanel";
import { PurchaseOrdersPanel } from "./features/purchase-orders/PurchaseOrdersPanel";
import { ReceivingPanel } from "./features/receiving/ReceivingPanel";
import { AgentDecisionsPanel } from "./features/agents/AgentDecisionsPanel";
import { ConsolidationPanel } from "./features/consolidation/ConsolidationPanel";
import { VendorOptimizationPanel } from "./features/vendor-optimization/VendorOptimizationPanel";
import { NegotiationsPanel } from "./features/negotiations/NegotiationsPanel";
import { AnalyticsPanel } from "./features/analytics/AnalyticsPanel";
import { AuditLogPanel } from "./features/audit/AuditLogPanel";
import { AutonomousOrderingPanel } from "./features/autonomous/AutonomousOrderingPanel";
import { UsersPanel } from "./features/admin/UsersPanel";
import { SuppliersPanel } from "./features/admin/SuppliersPanel";
import { ConnectorHealthPanel } from "./features/admin/ConnectorHealthPanel";

interface NavItem {
  path: string;
  icon: string;
  label: string;
  roles: string[] | null;
}

interface NavGroup {
  heading: string;
  items: NavItem[];
}

const NAV_GROUPS: NavGroup[] = [
  {
    heading: "Procure",
    items: [
      { path: "/rfqs", icon: "📋", label: "RFQ Board", roles: null },
      { path: "/requirements", icon: "🧾", label: "Requirements", roles: null },
      { path: "/parts", icon: "🔧", label: "Part Master", roles: null },
      {
        path: "/demand",
        icon: "➕",
        label: "New Requirement",
        roles: ["buyer", "planner"],
      },
      {
        path: "/purchase-orders",
        icon: "📦",
        label: "Purchase Orders",
        roles: ["buyer", "planner", "manager"],
      },
      {
        path: "/receiving",
        icon: "🔍",
        label: "Receiving",
        roles: ["buyer", "planner", "manager"],
      },
    ],
  },
  {
    heading: "AI Workforce",
    items: [
      {
        path: "/recommendations",
        icon: "🤖",
        label: "Recommendations",
        roles: null,
      },
      {
        path: "/consolidation",
        icon: "🧩",
        label: "Consolidation",
        roles: ["buyer", "planner", "manager", "admin"],
      },
      {
        path: "/negotiations",
        icon: "🤝",
        label: "Negotiations",
        roles: ["buyer", "planner", "manager", "admin"],
      },
      {
        path: "/vendor-optimization",
        icon: "📈",
        label: "Vendor Optimization",
        roles: ["manager", "admin"],
      },
      {
        path: "/autonomous-ordering",
        icon: "⚡",
        label: "Autonomous Ordering",
        roles: ["admin"],
      },
      {
        path: "/agents",
        icon: "🧠",
        label: "Agent Decisions",
        roles: ["manager", "finance"],
      },
    ],
  },
  {
    heading: "Insights",
    items: [
      {
        path: "/analytics",
        icon: "📊",
        label: "Analytics",
        roles: ["finance", "manager"],
      },
      {
        path: "/audit",
        icon: "🕓",
        label: "Audit Log",
        roles: ["finance", "manager"],
      },
    ],
  },
  {
    heading: "Administration",
    items: [
      { path: "/admin/users", icon: "👥", label: "Users", roles: ["admin"] },
      {
        path: "/admin/suppliers",
        icon: "🏭",
        label: "Suppliers",
        roles: ["admin"],
      },
      {
        path: "/admin/connectors",
        icon: "🔌",
        label: "Connector Health",
        roles: ["admin"],
      },
    ],
  },
  {
    heading: "System",
    items: [{ path: "/status", icon: "⚙", label: "API Status", roles: null }],
  },
];

function DemandPage() {
  const navigate = useNavigate();
  return (
    <>
      <PullTraxPanel onSuccess={() => void navigate("/rfqs")} />
      <DemandEntry onSuccess={() => void navigate("/rfqs")} />
    </>
  );
}

function AppLayout() {
  const navigate = useNavigate();
  const location = useLocation();

  const isAuthenticated = keycloak.authenticated === true;

  const { data: me = null } = useQuery({
    queryKey: ["me"],
    queryFn: api.getMe,
    enabled: isAuthenticated,
  });

  const [menuOpen, setMenuOpen] = useState(false);

  if (!isAuthenticated) return <LoginPage />;

  // Treat /rfqs/:id as the /rfqs nav item being active
  const activePath = location.pathname.startsWith("/rfqs/")
    ? "/rfqs"
    : location.pathname;

  // Navigate and close the mobile drawer in one step.
  const go = (path: string) => {
    void navigate(path);
    setMenuOpen(false);
  };

  return (
    <MeContext.Provider value={me}>
      <div style={S.shell}>
        <div
          className={`ap-backdrop${menuOpen ? " is-open" : ""}`}
          onClick={() => setMenuOpen(false)}
        />
        <aside
          className={`ap-sidebar${menuOpen ? " is-open" : ""}`}
          style={S.sidebar}
        >
          <div style={S.sidebarBrand}>
            <div
              style={{
                width: 38,
                height: 38,
                borderRadius: 11,
                display: "grid",
                placeItems: "center",
                fontSize: 19,
                color: "#fff",
                flexShrink: 0,
                background:
                  "linear-gradient(135deg, #4f46e5 0%, #4338ca 55%, #312e81 100%)",
                boxShadow: "0 6px 16px rgba(79,70,229,0.35)",
              }}
            >
              ✈
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{ ...S.h1, fontSize: 17, margin: 0 }}>
                AirProcurement
              </div>
              <div
                style={{
                  ...S.muted,
                  fontSize: 11,
                  letterSpacing: "0.02em",
                }}
              >
                Aviation procurement
              </div>
            </div>
          </div>

          <nav style={S.sidebarNav}>
            {NAV_GROUPS.map((group) => {
              const visible = group.items.filter(
                (it) => !it.roles || hasRole(me, ...it.roles),
              );
              if (visible.length === 0) return null;
              return (
                <div key={group.heading} style={{ marginBottom: 18 }}>
                  <div style={S.navGroupLabel}>{group.heading}</div>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 2,
                    }}
                  >
                    {visible.map(({ path, icon, label }) => {
                      const active = activePath === path;
                      return (
                        <button
                          key={path}
                          className={`ap-side-item${active ? " is-active" : ""}`}
                          style={S.navBtn(active)}
                          onClick={() => go(path)}
                        >
                          <span
                            style={{
                              width: 20,
                              textAlign: "center",
                              fontSize: 15,
                              flexShrink: 0,
                            }}
                          >
                            {icon}
                          </span>
                          <span>{label}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </nav>

          {me && (
            <div style={S.sidebarFooter}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  padding: "4px 6px 12px",
                  minWidth: 0,
                }}
              >
                <div
                  style={{
                    width: 34,
                    height: 34,
                    borderRadius: "50%",
                    display: "grid",
                    placeItems: "center",
                    fontSize: 14,
                    fontWeight: 700,
                    color: "#fff",
                    flexShrink: 0,
                    background:
                      "linear-gradient(135deg, #4f46e5 0%, #4338ca 100%)",
                  }}
                >
                  {me.email.charAt(0).toUpperCase()}
                </div>
                <div style={{ minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: 12.5,
                      fontWeight: 600,
                      color: "var(--ink)",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                    title={me.email}
                  >
                    {me.email}
                  </div>
                  <span style={S.roleBadge(primaryRole(me))}>
                    {primaryRole(me)}
                  </span>
                </div>
              </div>
              <button
                className="ap-iconbtn"
                style={{ ...S.btnGhost, width: "100%" }}
                onClick={() =>
                  void keycloak.logout({ redirectUri: window.location.origin })
                }
              >
                Logout
              </button>
            </div>
          )}
        </aside>

        <main style={S.mainCol}>
          <div className="ap-mobilebar">
            <button
              className="ap-hamburger"
              aria-label="Open navigation menu"
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen(true)}
            >
              ☰
            </button>
            <span style={{ fontWeight: 700, fontSize: 15 }}>
              ✈ AirProcurement
            </span>
          </div>
          <div style={S.content}>
            <div className="ap-fade-in" key={activePath}>
              <Routes>
                <Route index element={<Navigate to="/rfqs" replace />} />
                <Route
                  path="/rfqs"
                  element={
                    <RFQList
                      onSelectRFQ={(id) => void navigate(`/rfqs/${id}`)}
                    />
                  }
                />
                <Route path="/rfqs/:rfqId" element={<QuoteComparisonRoute />} />
                <Route
                  path="/requirements"
                  element={<RequirementsPanel me={me} />}
                />
                <Route path="/parts" element={<PartsPanel />} />
                <Route
                  path="/demand"
                  element={
                    <ProtectedRoute
                      roles={["buyer", "planner"]}
                      feature="New Requirement"
                    >
                      <DemandPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/recommendations"
                  element={<RecommendationsPanel me={me} />}
                />
                <Route
                  path="/purchase-orders"
                  element={
                    <ProtectedRoute
                      roles={["buyer", "planner", "manager"]}
                      feature="Purchase Orders"
                    >
                      <PurchaseOrdersPanel me={me} />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/receiving"
                  element={
                    <ProtectedRoute
                      roles={["buyer", "planner", "manager"]}
                      feature="Receiving"
                    >
                      <ReceivingPanel me={me} />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/analytics"
                  element={
                    <ProtectedRoute
                      roles={["finance", "manager"]}
                      feature="Analytics"
                    >
                      <AnalyticsPanel me={me} />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/agents"
                  element={
                    <ProtectedRoute
                      roles={["manager", "finance"]}
                      feature="Agent Decisions"
                    >
                      <AgentDecisionsPanel />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/audit"
                  element={
                    <ProtectedRoute
                      roles={["finance", "manager"]}
                      feature="Audit Log"
                    >
                      <AuditLogPanel />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/consolidation"
                  element={
                    <ProtectedRoute
                      roles={["buyer", "planner", "manager", "admin"]}
                      feature="Consolidation"
                    >
                      <ConsolidationPanel me={me} />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/vendor-optimization"
                  element={
                    <ProtectedRoute
                      roles={["manager", "admin"]}
                      feature="Vendor Optimization"
                    >
                      <VendorOptimizationPanel />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/negotiations"
                  element={
                    <ProtectedRoute
                      roles={["buyer", "planner", "manager", "admin"]}
                      feature="Negotiations"
                    >
                      <NegotiationsPanel me={me} />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/autonomous-ordering"
                  element={
                    <ProtectedRoute
                      roles={["admin"]}
                      feature="Autonomous Ordering"
                    >
                      <AutonomousOrderingPanel />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/users"
                  element={
                    <ProtectedRoute roles={["admin"]} feature="Users">
                      <UsersPanel />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/suppliers"
                  element={
                    <ProtectedRoute roles={["admin"]} feature="Suppliers">
                      <SuppliersPanel />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/connectors"
                  element={
                    <ProtectedRoute
                      roles={["admin"]}
                      feature="Connector Health"
                    >
                      <ConnectorHealthPanel />
                    </ProtectedRoute>
                  }
                />
                <Route path="/status" element={<HealthPanel />} />
                <Route path="*" element={<Navigate to="/rfqs" replace />} />
              </Routes>
            </div>
          </div>
        </main>
      </div>
    </MeContext.Provider>
  );
}

export default function App() {
  return <AppLayout />;
}
